import { GetServerSideProps } from 'next';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { AdvanceSearch } from '@app/components/home/AdvanceSearch';
import { Breadcrumb } from '@app/components/home/Breadcrumb';
import { PageTitle } from '@app/components/home/PageTitle';
import ArrowDownIcon from '@app/components/icons/ArrowDownIcon';
import GridIcon from '@app/components/icons/GridIcon';
import ListIcon from '@app/components/icons/ListIcon';
import PlusIcon from '@app/components/icons/PlusIcon';
import { useToast } from '@app/components/ui';
import { Button } from '@app/components/ui/Button';
import Checkbox from '@app/components/ui/Checkbox';
import Dropdown from '@app/components/ui/Dropdown';
import { Paginate } from '@app/components/ui/Paginate';
import { withLayout } from '@app/components/withLayout';
import { useAuth } from '@app/hooks/useAuth';
import { useHeadMeta } from '@app/hooks/useHeadMeta';
import { cn } from '@app/libs/utils';
import { newsService } from '@app/services/portal/news.services';
import { Properties } from '@components/home/Properties';

const MODES = [
  { label: 'Danh sách', value: 'list', icon: <ListIcon className="w-5 h-5" /> },
  { label: 'Dạng lưới', value: 'grid', icon: <GridIcon className="w-5 h-5" /> },
] as const;

export const SORT_LABELS: Record<string, string> = {
  'createdAt:desc': 'Tin mới nhất',
  'price:asc': 'Giá tăng dần',
  'price:desc': 'Giá giảm dần',
  'area:asc': 'Diện tích tăng dần',
  'area:desc': 'Diện tích giảm dần',
} as const;
const SORTS = Object.entries(SORT_LABELS).map(([value, label]) => ({ value, label }));

export const getServerSideProps: GetServerSideProps = async ctx => {
  try {
    const {
      keyword,
      address,
      types,
      bedRoom,
      bathRoom,
      fromPrice,
      toPrice,
      fromArea,
      toArea,
      orientation,
      furnitureType,
      provinceCodes,
      lat,
      lng,
      distance,
      wardCodes,
      orderBy,
      pageIndex = 1,
      pageSize = 12,
    } = ctx.query;
    const { data: properties, totalItem } = await newsService
      .setServerContext(ctx.req, ctx.res)
      .setCookies(ctx.req.headers.cookie || '')
      .list({
        pageIndex,
        pageSize,
        keyword,
        address,
        types,
        bedRoom,
        bathRoom,
        fromPrice,
        toPrice,
        fromArea,
        toArea,
        orientation,
        furnitureType,
        ...(lat && lng && distance !== undefined
          ? { lat, lng, distance }
          : { provinceCodes, wardCodes }),
        orderBy,
      });

    return {
      props: {
        properties,
        totalItem,
      },
    };
  } catch {
    return {
      props: {
        properties: [],
        totalItem: 0,
      },
    };
  }
};

const PropertiesPage: React.FC<{ properties: any[]; totalItem: number }> = ({
  properties: initialProperties,
  totalItem,
}) => {
  const toast = useToast();
  const { setTitle, setDescription, setKeywords } = useHeadMeta();
  const { isAuth } = useAuth();
  const [mode, setMode] = useState<'list' | 'grid'>();
  const [sort, setSort] = useState<any>();
  const [verifiedPriority, setVerifiedPriority] = useState<boolean>(false);
  const [page, setPage] = useState<number>();
  const [limit, setLimit] = useState(12);
  const [total, setTotal] = useState(totalItem);
  const [properties, setProperties] = useState<any[]>(initialProperties);
  const [loading, setLoading] = useState(false);
  const queryParams = useSearchParams();
  const filters = useRef<any>({});
  const debouncedRef = useRef<any>(null);

  useEffect(() => {
    setTitle('Danh sách tin bán');
    setDescription('Danh sách bất động sản');
    setKeywords('tin bán, bất động sản, bất động sản việt nam');
  }, [setTitle, setDescription, setKeywords]);

  useEffect(() => {
    const orderBy = queryParams.get('orderBy')?.split(',');
    const pageIndex = parseInt(queryParams.get('pageIndex') || '1');
    const pageSize = parseInt(queryParams.get('pageSize') || '12');
    setVerifiedPriority(orderBy?.includes('isConfirmed:desc') || false);
    setSort(orderBy?.slice(-1)[0] ?? 'createdAt:desc');
    setPage(pageIndex);
    setLimit(pageSize);
  }, [queryParams]);

  useEffect(() => {
    const mode = localStorage.getItem('properties_mode') || 'grid';

    setMode(mode as 'list' | 'grid');
  }, []);

  useEffect(() => {
    localStorage.setItem('properties_mode', mode ?? 'grid');
  }, [mode]);

  const handleSearch = async (params: any, pageIndex: number = 1, pageSize: number = 12) => {
    if (debouncedRef.current) {
      clearTimeout(debouncedRef.current);
    }

    debouncedRef.current = setTimeout(async () => {
      setLoading(true);
      filters.current = params;
      setPage(pageIndex);
      setLimit(pageSize);

      const orderBy = [
        ...[verifiedPriority ? 'isConfirmed:desc' : null].filter(Boolean),
        ...[sort ? sort : null].filter(Boolean),
      ];

      if (params.lat && params.lng && params.distance !== undefined) {
        delete params.provinceCodes;
        delete params.wardCodes;
      } else {
        delete params.lat;
        delete params.lng;
        delete params.distance;
      }

      const query = new URLSearchParams();
      Object.entries(params).forEach(([key, value]: any) => {
        if (value === undefined || value === null || value.length === 0) {
          delete params[key];

          return;
        }

        if (Array.isArray(value) && value.length > 0) {
          const joined = value.join(',');
          if (joined === '') {
            delete params[key];

            return;
          }

          query.set(key, joined);
        } else {
          query.set(key, value);
        }
      });
      query.set('pageIndex', pageIndex.toString());
      query.set('pageSize', pageSize.toString());

      if (orderBy.length > 0) {
        query.append('orderBy', orderBy.join(','));
      }

      window.history.replaceState(null, '', `?${query.toString()}`);

      try {
        const { data: properties, totalItem } = await newsService.list({
          pageIndex,
          pageSize: params.lat ? 500 : pageSize,
          ...params,
          orderBy,
        });

        setProperties(properties);
        setTotal(totalItem);
      } catch {
        toast.error('Lỗi khi tải dữ liệu. Xin vui lòng thử lại.');
        setProperties([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }

      setLoading(false);
    }, 1000);
  };

  return (
    <>
      <div className="bg-gray-200/80 -mb-15">
        <Breadcrumb
          items={[{ label: 'Tin bán', href: '/properties' }]}
          className={cn('w-auto 2xl:w-[90rem] p-5 sm:px-6 text-gray-900 text-sm flex mx-auto')}
        />
      </div>
      <div className="w-full 2xl:w-[90rem] p-5 text-gray-900 text-sm flex flex-col gap-5 mx-auto -mb-10">
        <PageTitle title="Tin bán" description="Danh sách bất động sản">
          <Dropdown
            options={SORTS as any}
            value={sort}
            onChange={setSort}
            description={
              <Checkbox
                labelClassName="text-nowrap"
                label="Ưu tiên tin đã xác thực"
                checked={verifiedPriority}
                onChange={checked => setVerifiedPriority(checked)}
              />
            }
          >
            <Button className="flex items-center gap-2 text-sm text-gray-800 transition-all border-0 cursor-pointer">
              {SORT_LABELS[sort as string]}
              <ArrowDownIcon className="w-4 h-4" />
            </Button>
          </Dropdown>
          <div className="flex gap-2">
            {MODES.map(item => (
              <Button
                key={item.value}
                onClick={() => setMode(item.value)}
                tooltip={item.label}
                className={cn(
                  'flex items-center gap-2 text-sm text-gray-800 transition-all border p-1 cursor-pointer',
                  'hover:border-gray-300 rounded-lg',
                  item.value === mode ? 'border-gray-300 rounded-lg' : 'border-transparent'
                )}
              >
                {item.icon}
              </Button>
            ))}
          </div>
        </PageTitle>
        <AdvanceSearch onSearch={handleSearch} properties={properties} />
      </div>
      <Properties properties={properties} mode={mode} loading={loading} />
      <div className="grow" />
      <div className="w-full 2xl:w-[90rem] p-5 text-gray-900 text-sm flex flex-col gap-5 mx-auto -mb-10">
        <Paginate
          total={total}
          page={page ?? 1}
          pageSize={limit}
          onChange={(page, limit) => {
            handleSearch(filters.current, page, limit);
          }}
          digits={5}
          className="justify-center"
        />
      </div>
    </>
  );
};

export default withLayout(PropertiesPage, 'default');
function setDescription(arg0: string) {
  throw new Error('Function not implemented.');
}

function setKeywords(arg0: string) {
  throw new Error('Function not implemented.');
}

function setOgImage(arg0: string) {
  throw new Error('Function not implemented.');
}
